import { microsoftLoginFlow, submitLogin } from 'actions/auth';
import Divider from 'component/base/Divider';
import {
  MicrosoftAuthentication,
  useMicrosoftAuthInProgress,
  useNoAccountError,
} from 'component/base/Microsoft';
import LoginForm from 'component/form/LoginForm';
import language from 'config/language';
import { TestSelector } from 'enums/tests';
import { FORM_ERROR } from 'final-form';
import { ErrorResponse } from 'graph/client';
import { useAppDispatch } from 'hooks/reduxHooks';
import { parse } from 'qs';
import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Space from 'styled-space';

import { Alert, DisableChildren, Heading, Login } from './styles';

const LoginView = () => {
  const { search } = useLocation<{ next?: string }>();
  const dispatch = useAppDispatch();
  const { hasNoAccountError, dismissNoAccountError, noAccountErrorMessage } =
    useNoAccountError();

  const { msAuthInProgress, msAuthFlowHandlers } = useMicrosoftAuthInProgress(
    dismissNoAccountError,
  );

  const onAuthSuccess = useCallback(
    (code: string) => {
      const query = parse(search, { ignoreQueryPrefix: true });
      const next = (query.next ? query.next : undefined) as string;
      return dispatch(microsoftLoginFlow(code, next));
    },
    [dispatch, search],
  );

  const onSubmit = useCallback(
    async (values: { email: string; password: string }) => {
      const query = parse(search, { ignoreQueryPrefix: true });
      const next = query.next ? query.next : undefined;
      try {
        // @ts-ignore - TODO - refactor
        await dispatch(submitLogin(values, next));
        return null;
      } catch (e) {
        const error = e as ErrorResponse | undefined;

        if (error?.status === 401) {
          return { [FORM_ERROR]: language.auth.login.error_message };
        }
        return { [FORM_ERROR]: language.auth.login.server_error_message };
      }
    },
    [dispatch, search],
  );

  return (
    <Login>
      <Helmet title="Login" />
      <Heading>Sign in to BeeCastle</Heading>
      {hasNoAccountError && (
        <Alert
          data-test={
            TestSelector.AUTHENTICATION_SIGNIN_MICROSOFT_NOACCOUNT_ERROR
          }
        >
          <p>{`${noAccountErrorMessage}`}</p>
        </Alert>
      )}
      <MicrosoftAuthentication
        authType="signin"
        onAuthSuccess={onAuthSuccess}
        {...msAuthFlowHandlers}
      />
      <DisableChildren disabled={msAuthInProgress}>
        <Space mt="8px">
          <Divider>Or, sign in with your email</Divider>
        </Space>
        <LoginForm onSubmit={onSubmit} />
      </DisableChildren>
    </Login>
  );
};

export default LoginView;
